import * as React from 'react'

import {
  Alert,
  Box,
  FormComponent,
  InputCheckBox,
  Text,
} from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import moment from 'moment'
import { RRule } from 'rrule'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'

import enableDay from 'modules/payroll_bundle/lib/enableDay'
import type PolicyForm from 'modules/payroll_bundle/pages/settings/Components/PayrollPolicy/Form/PolicyForm'
import StartsOnDatePicker from 'modules/payroll_bundle/pages/settings/Components/PayrollPolicy/StartsOnDatePicker'
import Policy, { PeriodicityType } from 'modules/payroll_bundle/store/models/Policy'

interface Props {
  readonly?: boolean
  policy?: Policy | null
  policyForm: PolicyForm
  loadingPolicies: boolean
  addEmployeesToPolicy?: boolean
  setAddEmployeesToPolicy?: (addEmployeesToPolicy: boolean) => void
}

const Body = ({
  policy,
  readonly = false,
  policyForm,
  loadingPolicies,
  setAddEmployeesToPolicy,
  addEmployeesToPolicy,
}: Props) => {
  const periodicities = Policy.periodicities()

  if (policy?.attributes.id && addEmployeesToPolicy) {
    setAddEmployeesToPolicy && setAddEmployeesToPolicy(false)
  }

  const periodicityOptions: SelectOption[] = Object.keys(periodicities).map((key) => ({
    label: periodicities[key as PeriodicityType].label ?? '',
    value: key,
  }))

  const enableDayForPolicy = (day: Date | moment.Moment): boolean => {
    const momentDay = moment(day)
    const recurrence =
      periodicities[policyForm.get('periodicity').value as PeriodicityType].recurrence
    return enableDay({ day: momentDay, recurrence })
  }

  const startDateByPeriodicity = (periodicity: string): moment.Moment => {
    const options = RRule.parseString(
      periodicities[periodicity as PeriodicityType].recurrence ?? ''
    )
    options.dtstart = new Date(Date.UTC(2000, 1, 1))

    const rule = new RRule(options)
    const day = new Date()
    const firstDayInUTC = new Date(Date.UTC(day.getFullYear(), day.getMonth(), 1))
    const lastDayInUTC = new Date(Date.UTC(day.getFullYear(), day.getMonth() + 1, 0))

    return moment(rule.between(firstDayInUTC, lastDayInUTC, true)[0])
  }

  const onChangePeriodicity = (field: Field): void => {
    const startsOn = startDateByPeriodicity(field.value)
    policyForm.setValues({
      starts_on: startsOn.format('YYYY-MM-DD'),
      recurrence: periodicities[field.value as PeriodicityType].recurrence,
    })
  }

  if (!policy || loadingPolicies) return <Loading />

  return (
    <FormComponent padding={null}>
      <TextInput
        field={policyForm.get('name')}
        label={i18n.t('payroll_bundle.payroll_policy.form.name')}
        readonly={false}
      />

      <Select
        field={policyForm.get('periodicity')}
        label={i18n.t('payroll_bundle.payroll_policy.form.periodicity')}
        options={periodicityOptions}
        onChange={onChangePeriodicity}
        readonly={readonly}
      />
      <StartsOnDatePicker
        form={policyForm}
        label={i18n.t('payroll_bundle.payroll_policy.form.starts_on')}
        readonly={readonly}
        enableDay={enableDayForPolicy}
        periodicity={policyForm.get('periodicity').value}
      />
      {setAddEmployeesToPolicy && !policy.attributes.id && (
        <Box flexDirection="row" gap="s8" alignItems="center">
          <InputCheckBox
            checked={addEmployeesToPolicy || false}
            onClick={() => {
              setAddEmployeesToPolicy(!addEmployeesToPolicy)
            }}
          />
          <Text.Normal size="300">
            {i18n.t('payroll_bundle.payroll_policy.form.add_employee')}
          </Text.Normal>
        </Box>
      )}
      {setAddEmployeesToPolicy && (
        <Alert color="note">{i18n.t('payroll_bundle.payroll_policy.form.warning_message')}</Alert>
      )}
    </FormComponent>
  )
}

export default observer(Body)
